/**
 * TrainingContext
 *
 * Global provider that connects the training system to the rest of the app.
 * Provides `trackFeature()` to record feature usage from any component,
 * manages achievement checking, and renders achievement popups.
 *
 * This is the integration layer that was missing — without it, the training
 * infrastructure (useTrainingProfile, useAchievements, Cloud Functions) sat
 * completely disconnected from actual user actions.
 */

import React, { createContext, useContext, useCallback, useRef } from 'react';
import { useTrainingProfile } from '../hooks/useTrainingProfile';
import { useAchievements } from '../features/training/achievements/useAchievements';
import { AchievementPopup } from '../features/training/achievements/AchievementPopup';
import type { TrainingFeatureId, TrainingProfile } from '../features/training/types';
import { getLevelFromXp } from '../features/training/types';
import { useAuth } from './AuthContext';

interface TrainingContextValue {
  /** Record that the user used a feature. Debounced per feature per session. */
  trackFeature: (featureId: TrainingFeatureId, milestone?: string) => void;
  /** The user's training profile (null if not loaded yet) */
  profile: TrainingProfile | null;
  /** Whether the profile is still loading */
  isLoading: boolean;
  /** Current level derived from XP */
  currentLevel: ReturnType<typeof getLevelFromXp>;
}

const TrainingContext = createContext<TrainingContextValue | null>(null);

/**
 * Map from ProjectDetail tab IDs to TrainingFeatureIds.
 * Only tabs that correspond to trackable features are included.
 */
export const TAB_TO_FEATURE: Record<string, TrainingFeatureId> = {
  documents: 'documents',
  chat: 'chat',
  cim: 'cim',
  'sector-research': 'sector-research',
  'transaction-search': 'transaction-search',
  'ic-committee': 'ic-committee',
  'ic-memo': 'ic-memo',
  presentations: 'presentations',
  infographics: 'infographics',
  legal: 'legal',
  'competitive-intel': 'competitive-intel',
  'customer-cube': 'customer-cube',
  questions: 'questions',
  'model-generator': 'model-generator',
};

export const TrainingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const {
    profile,
    isLoading,
    recordFeatureUsage,
    currentLevel,
  } = useTrainingProfile();
  const { pendingAchievement, dismissAchievement } = useAchievements(profile);

  // Debounce: only track each feature once per 60 seconds to avoid spamming
  const lastTrackedRef = useRef<Record<string, number>>({});

  const trackFeature = useCallback(
    (featureId: TrainingFeatureId, milestone?: string) => {
      if (!user?.uid) return;

      const key = milestone ? `${featureId}:${milestone}` : featureId;
      const now = Date.now();
      const lastTracked = lastTrackedRef.current[key] || 0;

      // Debounce: skip if tracked in the last 60 seconds (unless it's a milestone)
      if (!milestone && now - lastTracked < 60_000) return;

      lastTrackedRef.current[key] = now;
      recordFeatureUsage(featureId, milestone);
    },
    [user?.uid, recordFeatureUsage]
  );

  return (
    <TrainingContext.Provider value={{ trackFeature, profile, isLoading, currentLevel }}>
      {children}
      {/* Global achievement popup */}
      {pendingAchievement && (
        <AchievementPopup
          achievement={pendingAchievement}
          onClose={dismissAchievement}
        />
      )}
    </TrainingContext.Provider>
  );
};

/**
 * Hook to access training tracking from any component.
 * Returns a no-op trackFeature if called outside the provider (safe for SSR/tests).
 */
export function useTraining(): TrainingContextValue {
  const context = useContext(TrainingContext);
  if (!context) {
    return {
      trackFeature: () => {},
      profile: null,
      isLoading: false,
      currentLevel: getLevelFromXp(0),
    };
  }
  return context;
}
